'use client';

/**
 * Root-level error page. Next.js renders this when an error escapes the
 * root layout itself — the one place the app-shell ErrorBoundary cannot
 * catch. It replaces the entire document, so it must render its own
 * <html>/<body> and cannot rely on any provider, stylesheet, or context.
 *
 * PostHog is booted here directly (idempotent, self-gating): in this
 * page's primary scenario the root layout crashed before PostHogProvider
 * ever mounted, so waiting for the provider would mean never reporting
 * exactly the crashes this page exists for.
 */

import { useEffect } from 'react';
import { initPostHog, isPostHogActive, posthog } from '@/lib/analytics/posthog';
import { ErrorFallback } from '@/components/ErrorFallback';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Root layout error:', error);
    initPostHog();
    if (isPostHogActive()) {
      posthog.captureException(error, {
        source: 'global_error_page',
        digest: error.digest,
      });
    }
  }, [error]);

  return (
    <html lang="en">
      <body style={{ fontFamily: 'system-ui, sans-serif', margin: 0 }}>
        <ErrorFallback onRetry={reset} />
      </body>
    </html>
  );
}
